/**
 * What the PDP's price card shows, taken from the server's wallpaper quote
 * (POST /api/site/wallpapers/quote) and nothing else.
 *
 * The quote answers in one of two shapes:
 *   - with an `optimization` block → the walls were planned together (cross-wall
 *     billing): the paper, wastage and money come from that block;
 *   - without it (the feature is off) → each wall is cut on its own: the same
 *     figures come from the existing per-wall keys.
 *
 * Both map to one summary, so the template never knows which. Nothing is
 * rounded, multiplied or summed here — every number is one the server sent.
 * The admin panel's PDP maps the same response the same way
 * (mahattaart-admin-frontend-new: src/features/sales/lib/wallpaperQuote.ts).
 */

export interface PdpQuoteWallLine {
  /** The wall's own area, sq ft. */
  wallAreaSqft: number;
  /** The paper this wall is billed on — its share of the plan when optimized. */
  consumedSqft: number;
  /** This wall's wallpaper value excl. GST — the lines add up to `taxable` (no discount on the site). */
  base: number;
  /** This wall's wallpaper amount, incl. GST. */
  amount: number;
}

/** Installation as the server resolved it for the pincode. */
export interface PdpQuoteInstallation {
  /** The area it is charged on — the walls' area, never the billed paper. */
  sqft: number;
  /** ₹/sq ft for the pincode. Null when the response predates it. */
  rate: number | null;
  /** The server's decision: the city's fixed amount (true) or area × rate. Null when the response predates it. */
  fixed: boolean | null;
  /** Charge excl. GST. */
  base: number;
  gstPer: number;
  gst: number;
}

export interface PdpQuoteSummary {
  /** true = the walls were planned together and billed on the shared plan. */
  optimized: boolean;
  /** The walls as measured, sq ft. Shown, never billed. */
  wallAreaSqft: number;
  /** Paper billed. Wastage is inside it, never added on top. */
  consumedSqft: number;
  /** Paper billed minus the walls it covers (billed − wall area), not cutting scrap. */
  wastageSqft: number;
  wastagePct: number;
  /** Paper saved against cutting each wall on its own. Null when billed per wall. */
  savedSqft: number | null;
  /** The surface's rate, ₹/sq ft, excl. GST — what the billed sq ft is multiplied by. */
  rateExGst: number;
  /** The same rate incl. GST, as the surface chip quotes it. */
  rateIncl: number;
  gstPer: number;
  /** Billed sq ft × ex-GST rate (rounded once). */
  base: number;
  discount: number;
  taxable: number;
  gst: number;
  /** The wallpaper, incl. GST — before installation and the visit. */
  wallpaperTotal: number;
  /** Null when no installation is in the quote. */
  installation: PdpQuoteInstallation | null;
  /** What the customer pays, services included, to the rupee. */
  grandTotal: number;
  /** One line per wall when two or more were quoted; empty for a single wall. */
  walls: PdpQuoteWallLine[];
}

/** The parts of the quote response this summary reads. */
interface QuoteBody {
  wall_sqft?: number;
  billable_sqft?: number;
  waste_sqft?: number;
  waste_pct?: number;
  rate_ex_gst?: number;
  rate_incl?: number;
  gst_per?: number;
  printCost?: number;
  amount_payable?: number;
  total?: number;
  walls?: QuoteBody[];
  summary?: {
    baseAmount?: number;
    discountAmount?: number;
    taxableAmount?: number;
    gstTotal?: number;
    installationSqft?: number;
    installationRate?: number;
    installationFixed?: boolean;
    installationBase?: number;
    installationGstPer?: number;
    installationGst?: number;
  };
  optimization?: {
    totals: { totalWallAreaSqft: number; consumedSqft: number; wastageSqft: number; wastagePct: number; savedSqft: number };
    walls: Array<{ wallAreaSqft: number; allocatedConsumedSqft: number }>;
    pricing: {
      rateExGst?: number;
      gstPer?: number;
      baseAmount?: number;
      discountAmount?: number;
      taxableAmount?: number;
      gstAmount?: number;
      wallpaperTotal: number;
      grandTotal: number;
      walls: Array<{ baseAmount?: number; lineTotal: number }>;
    };
  };
}

/** Installation from the cart-level summary, or null when none is in the quote. */
function installationOf(response: QuoteBody): PdpQuoteInstallation | null {
  const s = response.summary;
  if (!s || !(Number(s.installationBase) > 0)) return null;
  return {
    sqft: s.installationSqft ?? response.wall_sqft ?? 0,
    rate: s.installationRate ?? null,
    fixed: s.installationFixed ?? null,
    base: s.installationBase ?? 0,
    gstPer: s.installationGstPer ?? 0,
    gst: s.installationGst ?? 0,
  };
}

/** The quote as the price card shows it, or null when there is nothing to show. */
export function toQuoteSummary(response: QuoteBody | null | undefined): PdpQuoteSummary | null {
  if (!response) return null;
  const perWall = Array.isArray(response.walls) && response.walls.length > 1 ? response.walls : [];
  // Read the block only when it is whole: one missing part (a partial or
  // malformed response) falls back to the per-wall keys rather than throwing and
  // taking the price card down with it.
  const optimization = response.optimization?.totals && response.optimization?.pricing && Array.isArray(response.optimization?.walls)
    ? response.optimization
    : undefined;

  const installation = installationOf(response);
  const rateIncl = response.rate_incl ?? 0;

  if (optimization) {
    const p = optimization.pricing;
    return {
      optimized: true,
      wallAreaSqft: optimization.totals.totalWallAreaSqft,
      consumedSqft: optimization.totals.consumedSqft,
      wastageSqft: optimization.totals.wastageSqft,
      wastagePct: optimization.totals.wastagePct,
      savedSqft: optimization.totals.savedSqft,
      rateExGst: p.rateExGst ?? response.rate_ex_gst ?? 0,
      rateIncl,
      gstPer: p.gstPer ?? response.gst_per ?? 0,
      base: p.baseAmount ?? 0,
      discount: p.discountAmount ?? 0,
      taxable: p.taxableAmount ?? 0,
      gst: p.gstAmount ?? 0,
      wallpaperTotal: p.wallpaperTotal,
      installation,
      grandTotal: p.grandTotal,
      walls: optimization.walls.length > 1
        ? optimization.walls.map((wall, i) => ({
          wallAreaSqft: wall.wallAreaSqft,
          consumedSqft: wall.allocatedConsumedSqft,
          base: p.walls[i]?.baseAmount ?? 0,
          amount: p.walls[i]?.lineTotal ?? 0,
        }))
        : [],
    };
  }

  const s = response.summary;
  return {
    optimized: false,
    wallAreaSqft: response.wall_sqft ?? 0,
    consumedSqft: response.billable_sqft ?? 0,
    wastageSqft: response.waste_sqft ?? 0,
    wastagePct: response.waste_pct ?? 0,
    savedSqft: null,
    rateExGst: response.rate_ex_gst ?? 0,
    rateIncl,
    gstPer: response.gst_per ?? 0,
    base: s?.baseAmount ?? 0,
    discount: s?.discountAmount ?? 0,
    taxable: s?.taxableAmount ?? 0,
    gst: s?.gstTotal ?? 0,
    wallpaperTotal: response.amount_payable ?? 0,
    installation,
    grandTotal: response.total ?? 0,
    walls: perWall.map(wall => ({
      wallAreaSqft: wall.wall_sqft ?? 0,
      consumedSqft: wall.billable_sqft ?? 0,
      base: wall.printCost ?? 0,
      amount: wall.amount_payable ?? 0,
    })),
  };
}

/** "walls.2" or "walls.2.width" → 2. */
const WALL_ERROR_KEY = /^walls\.(\d+)(?:\.|$)/;

export interface PdpQuoteErrors {
  /** Index into the PDP's own `walls` → the server's message for that wall. */
  byWall: { [wallIndex: number]: string };
  /** Anything not about one wall (surface, pincode…). */
  general: string | null;
}

/**
 * The errors after the wall at `removedIndex` is deleted: its own error goes, and
 * every later wall's error moves up one place WITH its wall — so a message never
 * lands under a different wall while the next quote is still to come.
 */
export function withoutWall(errors: PdpQuoteErrors, removedIndex: number): PdpQuoteErrors {
  const byWall: { [wallIndex: number]: string } = {};
  for (const key of Object.keys(errors.byWall)) {
    const wallIndex = Number(key);
    if (wallIndex < removedIndex) byWall[wallIndex] = errors.byWall[wallIndex];
    else if (wallIndex > removedIndex) byWall[wallIndex - 1] = errors.byWall[wallIndex];
  }
  return { byWall, general: errors.general };
}

/**
 * A 422 body split into per-wall and general messages.
 *
 * The quote only carries the MEASURED walls, so the server's `walls.{n}` is the
 * n-th quoted wall; `quotedWallIndexes` maps it back to the wall on the page.
 */
export function quoteErrorsFrom(
  body: { message?: string; errors?: { [key: string]: string[] } } | null | undefined,
  quotedWallIndexes: number[],
  fallback: string,
): PdpQuoteErrors {
  const byWall: { [wallIndex: number]: string } = {};
  let general: string | null = null;
  for (const key of Object.keys(body?.errors ?? {})) {
    const message = body!.errors![key]?.[0];
    if (!message) continue;
    const match = WALL_ERROR_KEY.exec(key);
    if (match) {
      const wallIndex = quotedWallIndexes[Number(match[1])] ?? Number(match[1]);
      if (byWall[wallIndex] === undefined) byWall[wallIndex] = message;
    } else if (general === null) {
      general = message;
    }
  }
  if (general === null && Object.keys(byWall).length === 0) general = body?.message || fallback;
  return { byWall, general };
}
